import { useState, useCallback } from 'react';
import i18n from '@/settings/i18n';
import { isProduction } from '@/settings/environment';

type UseTurnstileReturn = {
  // State
  turnstileToken: string | undefined;
  turnstileError: string;
  isLoading: boolean;

  // Callbacks（Turnstileコンポーネントに渡す）
  onSuccess: (token: string) => void;
  onError: () => void;

  // ユーティリティ
  resetError: () => void;
  resetToken: () => void;

  // ignoreTurnstile（フロント専用）を除外し、turnstile_token を追加したAPIパラメータを生成
  createParamsWithTurnstile: <T extends { ignoreTurnstile?: boolean }>(
    values: T,
  ) => {
    data: Omit<T, 'ignoreTurnstile'> & { turnstile_token: string };
    isValid: boolean;
  };
};

export const useTurnstile = (): UseTurnstileReturn => {
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(
    undefined,
  );
  const [turnstileError, setTurnstileError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const onSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileError('');
    setIsLoading(false);
  }, []);

  const onError = useCallback(() => {
    setTurnstileError(i18n.t('ui.turnstile.error'));
  }, []);

  const resetError = useCallback(() => {
    setTurnstileError('');
  }, []);

  const resetToken = useCallback(() => {
    setTurnstileToken(undefined);
  }, []);

  const createParamsWithTurnstile = useCallback(
    <T extends { ignoreTurnstile?: boolean }>(values: T) => {
      const { ignoreTurnstile, ...restValues } = values;

      // 開発環境でスキップチェックボックスが有効な場合
      if (!isProduction() && ignoreTurnstile) {
        return {
          data: { ...restValues, turnstile_token: 'SKIP_VERIFICATION' },
          isValid: true,
        };
      }

      // トークンがない場合は無効
      if (!turnstileToken) {
        setTurnstileError(i18n.t('ui.turnstile.error'));
        return {
          data: { ...restValues, turnstile_token: '' },
          isValid: false,
        };
      }

      return {
        data: { ...restValues, turnstile_token: turnstileToken },
        isValid: true,
      };
    },
    [turnstileToken],
  );

  return {
    turnstileToken,
    turnstileError,
    isLoading,
    onSuccess,
    onError,
    resetError,
    resetToken,
    createParamsWithTurnstile,
  };
};
